import {
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayConfig,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TooltipOverlayComponent } from '../tooltip-overlay/tooltip-overlay.component';
import { filter, first } from 'rxjs';
import { getValue, isDefined } from '../../../../shared/utility/verify.lib';
import {
  currentStep,
  storedExportSettings,
  storedSession,
} from '../../../../redux/mydoc.selector';
import { Store } from '@ngrx/store';
import { ISession } from '../../../../models/session/session.models';
import { SubscriptionComponent } from '../../../../shared/subscription/subscription.component';
import { getExportSettings } from '../../../../redux/mydoc.action';
import { IExportSettingsFe } from '../../../../models/export/export-settings-fe.model';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-customize-toggle',
  templateUrl: './customize-toggle.component.html',
  styleUrl: './customize-toggle.component.scss',
})
export class CustomizeToggleComponent
  extends SubscriptionComponent
  implements OnInit
{
  @Input() public overlayMode: boolean = false;
  @Output() public exportToggleHandler: EventEmitter<boolean> =
    new EventEmitter();
  @ViewChild('customToggle') public customToggle: ElementRef;

  public tooltipText: string;
  public isTooltipDisabled: boolean = false;
  public isOverlayOpen: boolean = false;

  public storedSession$ = this.store.select(storedSession);
  public storedExportSettings$ = this.store.select(storedExportSettings);
  public currentStep$ = this.store.select(currentStep);
  private overlayRef: OverlayRef = null;

  public constructor(private overlay: Overlay, private store: Store) {
    super();
  }

  public ngOnInit(): void {
    this.initCurrentStep();
    if (!this.overlayMode) {
      const currentSession: ISession = this.getLatestValueFromSubscription(
        this.storedSession$
      );

      this.store.dispatch(
        getExportSettings({
          sessionId: getValue(() => currentSession.id),
          orderCode: getValue(() => currentSession.orderNumber),
        })
      );
    }
  }

  public onToggleChange(toggle: MatSlideToggleChange): void {
    this.exportToggleHandler.emit(getValue(() => toggle.checked, false));
  }

  public openTooltipOverlay(): void {
    if (!this.overlayMode) {
      this.isOverlayOpen = true;
      const target = this.customToggle.nativeElement as HTMLElement;
      const toogleHeight: number = getValue(() => target.offsetHeight);
      const paddingHeight: number = 24;
      const positionStrategy: FlexibleConnectedPositionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(target)
        .withPositions([
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: toogleHeight + paddingHeight,
          },
        ]);
      const overlayConfig: OverlayConfig = new OverlayConfig({
        hasBackdrop: true,
        positionStrategy: positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.block(),
      });

      this.overlayRef = this.overlay.create(overlayConfig);
      const filePreviewPortal: ComponentPortal<TooltipOverlayComponent> =
        new ComponentPortal(TooltipOverlayComponent);
      const ref: ComponentRef<TooltipOverlayComponent> =
        this.overlayRef.attach(filePreviewPortal);
      this.overlayRef
        .backdropClick()
        .pipe(first())
        .subscribe(() => {
          ref.instance.close();
        });
      ref.instance.closeHandler.pipe(first()).subscribe(() => {
        this.closeOverlay();
      });
      ref.instance.toogleHeight = toogleHeight + paddingHeight;
    }
  }

  private closeOverlay(): void {
    if (isDefined(() => this.overlayRef)) {
      this.isOverlayOpen = false;
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private initCurrentStep(): void {
    if (!this.overlayMode) {
      this.currentStep$
        .pipe(
          filter((step: number) => step === 2),
          first()
        )
        .subscribe(() => {
          const exportSettings: IExportSettingsFe =
            this.getLatestValueFromSubscription(this.storedExportSettings$);
          if (
            exportSettings?.showExportSettingsTooltip === true &&
            !this.isOverlayOpen
          ) {
            this.openTooltipOverlay();
          }
        });
    }
  }
}
